import React, { useEffect } from 'react';
import _ from 'lodash';
import { pathFromModuleId } from './parsePath';
import LimuirsContext from './LimuirsContext';

/**
 * A debounced function that emits an event when a LIMUIRS component
 * renders or updates. The event is emitted on the document object
 * and currently passes no additional context. This provides a mechanism
 * for non-LIMUIRS components to "react" when React components render.
 * This is needed since some React components proxy-render LIA components
 * that may need to have some JS run once they are in the DOM.
 */
const debouncedEmitRenderedComponentEvent = _.debounce(() => {
  if (document) {
    const event = document.createEvent('CustomEvent');
    event.initCustomEvent('LITHIUM:limuirsComponentRendered', true, true, {});
    document.dispatchEvent(event);
  }
}, 10);

/**
 * Sets {@see LimuirsContext} as props on wrapped component if the props are not already present.
 *
 * Please note that any existing LimuirsContext props will override ones set via the React 16 context.
 *
 * @param WrappedComponent
 * @param moduleId
 * @returns {function(*): *}
 */
const wrapContextIfNecessary = function(WrappedComponent, moduleId) {
  /* eslint react/display-name: "off" */
  return props => {
    useEffect(debouncedEmitRenderedComponentEvent);

    const path = pathFromModuleId(moduleId);
    return (
      <LimuirsContext.Consumer>
        {ctx => {
          // get component-specific text keys
          const text = ctx && ctx.req ? ctx.req.getTextViewForComponent(path) : props.text;
          return <WrappedComponent {...ctx} {...props} text={text} />;
        }}
      </LimuirsContext.Consumer>
    );
  };
};

/**
 * A higher order component that provides various parts of Limuirs functionality
 * such as global data, prefetch data, and i18n.
 *
 * @param module the component module.  Used for hot reloading.
 * @param {Object} WrappedComponent The component class to wrap.
 * @param {Boolean} inert Only render the component on the server.  Do not rehydrate on the client.  Default is false.
 * @returns {T} the wrapped component
 */
const limuirsComponent = function(module, WrappedComponent, { inert = false } = {}) {
  const comp = wrapContextIfNecessary(WrappedComponent, module.id);
  comp.inert = !!inert;
  return comp;
};

export default limuirsComponent;
